import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import FormTextField from '../../../components/ui/form/FormTextField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import {
  DESCRIPTION_MAX_LEN,
  NAME_MAX_LEN,
  STATIC_CONTENT_MAX_LEN,
  makeTemplateParameterFormSchema,
  type TemplateParameterFormValues,
} from './templateParameterFormSchema'
import type { StaticOption, TemplateParameter } from '../types'

const EMPTY: TemplateParameterFormValues = {
  nameTr: '',
  nameEn: '',
  descriptionTr: '',
  descriptionEn: '',
  staticContent: '',
}

export interface TemplateParameterFormDialogProps {
  visible: boolean
  /** Düzenlenen parametre. Kapalıyken `null` olur — form **yalnız düzenleme** içindir. */
  initial: TemplateParameter | null
  /**
   * "Statik mi?" seçenekleri. Form artık bu alanı **düzenlemediği** için liste bir *seçenek kaynağı*
   * değil: yalnız **"Evet" kimliğini** taşır (`yes` bayrağı) → kaydın statik olup olmadığı istemcide
   * sihirli sayı olmadan çözülür (research D14).
   */
  staticOptions: StaticOption[]
  submitting?: boolean
  onSubmit: (values: TemplateParameterFormValues) => void
  onHide: () => void
  /**
   * <b>Salt okunur görüntüleme</b> (126 — Kısıtlı Yetkili asistan): kaydetme düğmesi çizilmez, alanlar
   * `readOnly` olur. Açıklama ve statik içerik listede görünmediği için kayıt bu kiple açılır.
   * <b>Yazma yasağını alanların KENDİ `readOnly`'si sağlar</b>, `FormDialog`'un fieldset'i değil:
   * o artık `disabled` taşımıyor (THY AI review HIGH, 26.10.2026 — `disabled` metni klavyeyle
   * okunamaz yapıyordu). Yeni bir alan eklerken `readOnly={readOnly}` geçirmek ZORUNLUDUR.
   */
  readOnly?: boolean
}

/**
 * Şablon Parametresi **düzenleme** formu (059 US4).
 *
 * <p><b>Yeni parametre ekleme yoktur</b> (2026-08-04 kararı): parametreler şablon metnindeki yer
 * tutucuların kataloğudur — her kodun karşılığı gönderim tarafında çözülür, yani yeni bir kod ancak
 * kodda karşılığı yazıldığında anlam taşır. Bu yüzden ekleme/silme veri tabanı tarafında yapılır ve
 * başlık daima "Düzenle"dir.
 *
 * <p><b>Grup, parametre kodu ve "Statik mi?" salt-okunurdur</b> (görünür-pasif): kaydın ne olduğunu
 * belirledikleri için gösterilirler ama değiştirilemezler. Kod, yazılmış şablon metinlerinin içinde
 * geçer — değişmesi o metinlerdeki yer tutucuyu sessizce çözülemez hâle getirirdi; grup yalnız
 * sınıflandırmadır ve kayıt oluşturulurken belirlenir; <b>"Statik mi?" ise parametrenin türüdür</b>
 * (2026-08-05 kararı) — değeri nereden alacağını söyler ve bunu sonradan çevirmek, kaydın anlamını
 * yer tutucuyu kullanan şablonların altından değiştirir. Üçü de şemada yer almaz; gönderimde kaydın
 * kendi değerleri kullanılır (bkz. `templateParameterFormSchema`).
 *
 * **Statik içerik (FR-028):** alan yalnız kayıt <b>statikse</b> render edilir ve o durumda
 * <b>düzenlenebilir + zorunludur</b> (boş içerik şablonda boş bir yer tutucuya dönüşür). Statik olmayan
 * bir parametrede içerik <b>hiç anlam taşımaz</b> → alan hiç gösterilmez; sunucu da aynı garantiyi
 * yeniden uygular ("Statik mi? = Hayır" ise `staticContent` **`null`** yazılır).
 *
 * <p>"Statik mi?" artık düzenlenemediği için alanın görünürlüğü <b>form açılışında sabittir</b>:
 * belirip kaybolan bir alan ve "Evet → Hayır geçişinde içeriği temizle" mantığı kalmadı. "Evet"in
 * kimliği yine koda gömülmez — sunucudan gelen `StaticOption.yes` bayrağından türetilir (research
 * D14) → istemcide `10`/`11` sihirli sayısı **yoktur**.
 *
 * <p><b>Neden gizleme, neden devre dışı bırakma değil</b> (2026-08-03 kararı): devre dışı bir alan
 * "burada doldurulacak bir şey var ama şu an olmaz" der; oysa dinamik bir parametrede statik içerik
 * hiç anlam taşımaz. Alanı kaldırmak formu kısaltır ve yanlış beklenti üretmez.
 */
export default function TemplateParameterFormDialog({
  visible,
  initial,
  staticOptions,
  submitting,
  onSubmit,
  onHide,
  readOnly = false,
}: Readonly<TemplateParameterFormDialogProps>) {
  const { t } = useTranslation()

  const yesId = staticOptions.find((o) => o.yes)?.id ?? null

  /**
   * Kayıt **statik mi?** — "Evet" kimliği sunucudan gelen `yes` bayrağıyla çözülür (research D14).
   *
   * Seçenekler henüz gelmediyse `yesId` `null`'dır ve statik içerik alanı **bir an görünmez**; istek
   * çözülünce belirir. Bu bilinçli: alternatif, istemciye `11` sabitini gömmek olurdu. Alanın o anda
   * gizli olması veri kaybettirmez — değer `initial`'dan gelir, kullanıcı girdisi beklenmez.
   */
  const staticRecord = yesId != null && initial?.isStatic === yesId

  // Şema `staticRequired`'a bağlıdır (statik içerik zorunluluğu ona göre uygulanır).
  const schema = useMemo(() => makeTemplateParameterFormSchema(staticRecord), [staticRecord])
  const { control, handleSubmit, reset } = useZodForm(schema, {
    defaultValues: EMPTY,
  })

  /**
   * Formu doldurur. **Bağımlılıklar bilinçli olarak yalnız `[visible, initial, reset]`**: statik
   * seçenekler isteği geç çözülürse (`[] → [Evet, Hayır]`) efekt yeniden çalışmamalı — yoksa `reset()`
   * kullanıcının o ana kadar yazdığı her şeyi silerdi.
   */
  useEffect(() => {
    if (!visible) return
    reset(
      initial
        ? {
            nameTr: initial.nameTr,
            nameEn: initial.nameEn ?? '',
            descriptionTr: initial.descriptionTr ?? '',
            descriptionEn: initial.descriptionEn ?? '',
            staticContent: initial.staticContent ?? '',
          }
        : EMPTY,
    )
  }, [visible, initial, reset])

  return (
    <FormDialog
      visible={visible}
      header={
        readOnly
          ? t('admin.templateParameters.form.viewTitle')
          : t('admin.templateParameters.form.editTitle')
      }
      submitLabel={t('admin.templateParameters.form.save')}
      submitting={submitting}
      onSubmit={handleSubmit(onSubmit)}
      onHide={onHide}
      readOnly={readOnly}
      width="46rem"
    >
      <FormGrid>
        {/*
          Grup ve kod: görünür-pasif. Zorunluluk yıldızı TAŞIMAZLAR — yıldız "doldurman gerekiyor"
          demektir; burada kullanıcının dolduracağı bir şey yok.
        */}
        <FormField label={t('admin.templateParameters.form.group')} htmlFor="templateParamGroupName">
          <input
            id="templateParamGroupName"
            className="p-inputtext p-component p-disabled"
            value={initial?.templateParamGroupName ?? ''}
            readOnly
          />
        </FormField>
        <FormField label={t('admin.templateParameters.form.parameterCode')} htmlFor="parameterCode">
          <input
            id="parameterCode"
            className="p-inputtext p-component p-disabled"
            value={initial?.parameterCode ?? ''}
            readOnly
          />
        </FormField>
        <FormTextField
          readOnly={readOnly}
          control={control}
          name="nameTr"
          label={t('admin.templateParameters.form.nameTr')}
          required
          maxLength={NAME_MAX_LEN}
        />
        <FormTextField
          readOnly={readOnly}
          control={control}
          name="nameEn"
          label={t('admin.templateParameters.form.nameEn')}
          maxLength={NAME_MAX_LEN}
        />
        <FormTextareaField
          readOnly={readOnly}
          control={control}
          name="descriptionTr"
          label={t('admin.templateParameters.form.descriptionTr')}
          rows={2}
          maxLength={DESCRIPTION_MAX_LEN}
        />
        <FormTextareaField
          readOnly={readOnly}
          control={control}
          name="descriptionEn"
          label={t('admin.templateParameters.form.descriptionEn')}
          rows={2}
          maxLength={DESCRIPTION_MAX_LEN}
        />
        {/*
          "Statik mi?" formun SONUNDA durur ve statik içerik **hemen altında** görünür. Sıra bilinçlidir:
          içerik alanının VARLIĞINI belirleyen bilgi hemen üstünde olsun — kullanıcı "bu alan neden var /
          neden yok?" sorusunu tek bakışta yanıtlayabilsin. Değer artık **değişmediği** için görünürlük
          form açılışında sabittir (2026-08-05); alan yalnız kayıt statikse render edilir.
        */}
        <FormField label={t('admin.templateParameters.form.isStatic')} htmlFor="isStaticName">
          <input
            id="isStaticName"
            className="p-inputtext p-component p-disabled"
            value={initial?.isStaticName ?? ''}
            readOnly
          />
        </FormField>
        {staticRecord && (
          <FormTextareaField
            readOnly={readOnly}
            control={control}
            name="staticContent"
            label={t('admin.templateParameters.form.staticContent')}
            required
            rows={3}
            maxLength={STATIC_CONTENT_MAX_LEN}
          />
        )}
      </FormGrid>
    </FormDialog>
  )
}
